package com.flowyride.core.domain.analysis

import com.flowyride.core.domain.model.GeoPoint
import kotlin.math.abs

/** How a ride feels, derived from [Curvature.degreesPerKm]. */
enum class RideCharacter { FLOWING, PLEASANT, TWISTY, VERY_TWISTY }

/**
 * Cumulated heading changes per kilometre: 0 on a straight line, ~40 on motorways,
 * 250+ on mountain or countryside twisties. Engine independent, so routes can be compared.
 */
data class Curvature(val degreesPerKm: Double) {

    val character: RideCharacter
        get() = when {
            degreesPerKm < FLOWING_MAX -> RideCharacter.FLOWING
            degreesPerKm < PLEASANT_MAX -> RideCharacter.PLEASANT
            degreesPerKm < TWISTY_MAX -> RideCharacter.TWISTY
            else -> RideCharacter.VERY_TWISTY
        }

    private companion object {
        // Calibrated on French routes: fastest car routes 40-90, TomTom "thrilling" 250-350.
        const val FLOWING_MAX = 100.0
        const val PLEASANT_MAX = 200.0
        const val TWISTY_MAX = 300.0
    }
}

object CurvatureAnalyzer {

    /**
     * Points are first resampled every [RESAMPLING_METERS]: dense OSM geometry
     * (roundabouts, lane splits) would otherwise count as curves.
     *
     * @return null when the track is too short to be meaningful.
     */
    fun analyze(track: List<GeoPoint>): Curvature? {
        val distanceKm = track.zipWithNext { a, b -> a.distanceTo(b) }.sum() / 1_000
        if (distanceKm < MIN_DISTANCE_KM) return null

        val resampled = track.resample(RESAMPLING_METERS)
        val totalTurn = resampled
            .zipWithNext { a, b -> a.bearingTo(b) }
            .zipWithNext { from, to -> abs(headingChange(from, to)) }
            .sum()
        return Curvature(totalTurn / distanceKm)
    }

    private fun List<GeoPoint>.resample(stepMeters: Double): List<GeoPoint> {
        val kept = mutableListOf(first())
        for (point in drop(1)) {
            if (kept.last().distanceTo(point) >= stepMeters) kept += point
        }
        return kept
    }

    private const val RESAMPLING_METERS = 30.0
    private const val MIN_DISTANCE_KM = 0.5
}
